import { Sparkles, Check } from 'lucide-react';

import type { AssistProgress } from '../../api';

/**
 * The shoot page's account of Review Assist: what it is doing, or what it did.
 *
 * Reporting only. Starting a review is the header's job now — the offer is the
 * product's whole selling point and belongs beside the shoot's name, not in a
 * card three screens down. Keeping a second start button here would have given
 * the page two ways to do one thing and no obvious next step.
 *
 * Renders nothing at all when there is nothing to report. A shoot that has
 * never been reviewed says so through the header button; an empty card
 * underneath repeating the offer is noise.
 */
export default function ReviewAssistCard({
  progress,
  running,
  isDeep,
  onStop,
}: {
  progress: AssistProgress | null;
  running: boolean;
  isDeep: boolean;
  onStop: () => void;
}) {
  const report = running ? runningCopy(progress) : idleCopy(isDeep, progress);
  if (!report) return null;

  return (
    <section className="mt-8 flex flex-col gap-4 rounded-xl border border-ink-800 bg-ink-900 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
      <div className="min-w-0">
        <h2 className="flex items-center gap-2 text-head font-semibold text-ink-50">
          {isDeep && !running ? (
            <Check size={16} className="shrink-0 text-keep" />
          ) : (
            <Sparkles size={16} className="shrink-0 text-warn" />
          )}
          Review Assist
        </h2>

        <p className="mt-1.5 max-w-md text-caption leading-relaxed text-ink-400">{report}</p>

        {running && progress && <Bar progress={progress} />}
      </div>

      {running && (
        <button className="btn-ghost shrink-0 px-5 py-2.5 text-body" onClick={onStop}>
          Stop
        </button>
      )}
    </section>
  );
}

function Bar({ progress }: { progress: AssistProgress }) {
  // The download has its own numbers and its own scale; showing frame progress
  // during it would sit at zero for a minute and look stuck.
  const download = progress.download;
  const share = download
    ? download.total > 0
      ? download.loaded / download.total
      : 0
    : progress.total > 0
      ? progress.done / progress.total
      : 0;

  return (
    <div className="mt-4 flex items-center gap-3">
      <div className="h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-ink-850">
        <div
          className="h-full rounded-full bg-warn transition-[width] duration-300 ease-out"
          style={{ width: `${Math.min(1, share) * 100}%` }}
        />
      </div>
      <span className="tnum shrink-0 text-caption text-ink-500">
        {Math.round(Math.min(1, share) * 100)}%
      </span>
    </div>
  );
}

function runningCopy(progress: AssistProgress | null): string {
  if (!progress) return 'Starting…';
  if (progress.phase === 'loading') {
    return progress.download
      ? `Downloading the model: ${mb(progress.download.loaded)} of ${mb(progress.download.total)}.`
      : 'Getting the model ready. This happens once.';
  }
  if (progress.phase === 'grouping') {
    return `Grouping bursts: ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()} frames${
      progress.device === 'wasm' ? '. No GPU here, so this will take a while.' : '.'
    }`;
  }
  if (progress.phase === 'measuring') {
    // Named for what it is doing rather than for the machinery: this pass reads
    // the originals back to see which frames actually hit focus.
    return `Checking focus: ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()} frames.`;
  }
  return `Measuring sharpness: ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()}.`;
}

/** What there is to say once nothing is running. Null means: say nothing. */
function idleCopy(isDeep: boolean, progress: AssistProgress | null): string | null {
  // A review that read nothing and a review whose model would not load are very
  // different situations, and the second one still leaves the photographer with
  // something useful. Saying so is the difference between a warning and a lie.
  if (progress?.phase === 'failed') return `Could not run: ${progress.error}`;
  if (progress?.error) return `The model could not be loaded: ${progress.error}. Blurry frames are still flagged.`;
  if (isDeep) return 'Bursts grouped and your best shots marked. Suggestions only. Nothing was culled for you.';
  return null;
}

function mb(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}
